import { buildPreviewSafeAreaOverlay, formatPreviewSafeAreaLabel, formatPreviewSafeAreaTooltip } from 'common/preview-safe-area'

export default function fileMediaComponent ($log, BulkApplyService, FileMediaService, SceneCollectionService, SceneCollectionItemService, MediaLabelService, $q, CONFIGURATION) {
  'ngInject'
  return {
    restrict: 'E',
    scope: {},
    bindToController: {
      scene: '=?'
    },
    controller: function () {
      this.selectedLabel = null
      this.labelsInUse = []
      this.selectedDate = null
      this.sortOption = 'name'
      this.selectedMediaUsage = 'all'
      this.mediaInUse = {}
      this.S3url = FileMediaService.getS3MediaUrl()
      this.previewSafeAreaConfig = (CONFIGURATION && CONFIGURATION.previewSafeArea) || {}
      this.resolutionSort = { key: 'name', reverse: false }
      this.resolutionRows = []
    },
    controllerAs: 'ctrl',
    template: require('./file-media.tpl.html').default,
    link: function ($scope, $element, attrs, ctrl) {
      ctrl.$q = $q
      ctrl.$log = $log
      ctrl.isCollapsed = false
      ctrl.showFilters = false

      $scope.$watch(
        function () {
          return SceneCollectionService.activeSceneCollectionId
        },
        function (newValue, oldValue) {
          if (newValue !== oldValue) {
            ctrl.getMediaStats()
          }
        }
      )

      // Collections
      ctrl.FileMedia = FileMediaService.getAll()

      ctrl.unsubscribe = FileMediaService.subscribe(state => {
        ctrl.FileMedia = state.FileMedias
        fetchLabelsInUse()
      })

      // Setup subscription of MediaLabel relation changes
      ctrl._relationChangeUuid = MediaLabelService
        .subscribeToLabelRelationChanges('filemedias', relationInstance => {
          fetchLabelsInUse()
        })

      ctrl.getMediaStats = function () {
        if (!SceneCollectionService.activeSceneCollectionId) {
          ctrl.mediaInUse = {}
          return
        }

        // Try to use cached media stats from SceneCollectionItemService first - scoped to active scene collection
        const cachedStats = SceneCollectionItemService.getMediaStatsForSceneCollection(SceneCollectionService.activeSceneCollectionId)
        if (cachedStats && Object.keys(cachedStats).length > 0) {
          ctrl.mediaInUse = cachedStats
          ctrl.$log.debug('[FileMedia] Updated mediaInUse from cache for scene collection:', SceneCollectionService.activeSceneCollectionId, ctrl.mediaInUse)
          return
        }

        // Fallback to API call if no cached data available
        SceneCollectionItemService.getSceneCollectionItemsForSceneCollection(SceneCollectionService.activeSceneCollectionId)
          .then(response => {
            ctrl.mediaInUse = response.mediaStats || {}
            ctrl.$log.debug('[FileMedia] Updated mediaInUse from backend for scene collection:', SceneCollectionService.activeSceneCollectionId, ctrl.mediaInUse)
          })
      }

      // Subscribe to scene collection changes
      const sceneCollectionUnsubscribe = SceneCollectionService.subscribe(() => {
        // When activeSceneCollectionId changes, only refresh if we don't have current stats
        // This preserves existing data during transitions
        if (!SceneCollectionService.activeSceneCollectionId) {
          ctrl.mediaInUse = {}
        } else if (Object.keys(ctrl.mediaInUse).length === 0) {
          // Only fetch new data if we don't already have data
          ctrl.getMediaStats()
        }
      })

      // Subscribe to media stats updates (event-driven, no delays needed)
      const mediaStatsUnsubscribe = SceneCollectionItemService.subscribeToMediaStats((newMediaStats) => {
        // Always update media stats, including when they become empty (all media deleted)
        if (newMediaStats !== undefined && newMediaStats !== null) {
          ctrl.mediaInUse = newMediaStats
          $scope.$evalAsync()
        }
      }) // Functions
      ctrl.delete = id => {
        const confirmDelete = confirm('Are you sure you want to delete this media item?')
        if (confirmDelete) {
          FileMediaService.delete(id)
        }
      }
      ctrl.getDuration = id => FileMediaService.getDuration(id)
      ctrl.getEvent = id => FileMediaService.getEvent(id)
      ctrl.getOrientations = id => FileMediaService.getOrientations(id)
      ctrl.getThumbnailUrl = id => FileMediaService.getThumbnailUrl(id)
      ctrl.getType = id => FileMediaService.getType(id)
      ctrl.isSceneActive = function () {
        return !!SceneCollectionService.activeSceneCollectionId
      }

      ctrl.getIcon = id => {
        const type = FileMediaService.getType(id)
        if (type === 'image') {
          return 'fa-picture-o'
        } else if (type === 'video') {
          return 'fa-film'
        }
        return 'fa-question'
      }

      ctrl.toggleBulk = function toggleBulkFileMedia (filemediaId) {
        BulkApplyService.toggleElement('filemedias', filemediaId)
      }

      ctrl.addToScene = function addFileToScene (id) {
        SceneCollectionService.addSceneCollectionItemToSceneCollection('file', id)
      }

      $scope.$on('$destroy', () => {
        ctrl.unsubscribe()
        sceneCollectionUnsubscribe()
        mediaStatsUnsubscribe()
        MediaLabelService.unsubscribeToLabelRelationChanges('filemedias', ctrl._relationChangeUuid)
        BulkApplyService.destroyNamespace('filemedias')
      })

      // Code below is for filtering/sorting
      function fetchLabelsInUse () {
        const allLabels = MediaLabelService.getAll()
        ctrl.labelsInUse = allLabels.filter(item => item.filemedias && item.filemedias.length > 0)
        ctrl.labelsInUse.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      }

      ctrl.filterByLabel = function (item) {
        if (!ctrl.selectedLabel) return true
        const labelIds = item.labels.map(label => label.id)
        return labelIds.includes(ctrl.selectedLabel.id)
      }

      ctrl.filterByMediaUsage = function (item) {
        const mediaInUseCount = ctrl.mediaInUse[item.id] || 0

        switch (ctrl.selectedMediaUsage) {
          case 'inUse':
            return mediaInUseCount >= 1
          case 'notInUse':
            return mediaInUseCount === 0
          default:
            return true // for 'all' or any other case
        }
      }

      ctrl.getUniqueDates = function () {
        const uniqueDates = new Set()
        ctrl.FileMedia.forEach(item => {
          const date = item.createdAt.slice(0, 10)
          uniqueDates.add(date)
        })
        return Array.from(uniqueDates).sort()
      }

      ctrl.filterByDate = function (date) {
        if (date) {
          return item => {
            return item.createdAt.slice(0, 10) === date
          }
        }
        return item => true
      }

      ctrl.clearFilters = function () {
        ctrl.selectedDate = null
        ctrl.selectedLabel = null
        ctrl.sortOption = 'name'
        ctrl.selectedMediaUsage = 'all'
      }

      ctrl.getDurationSort = function (item) {
        return FileMediaService.getDuration(item.id)
      }

      ctrl.getMediaInUseSort = function (item) {
        // Return the negative count for descending order. Items with higher counts come first.
        // If the item is not in mediaInUse, default to 0 to handle undefined counts.
        return -(ctrl.mediaInUse[item.id] || 0)
      }

      ctrl.getCreatedAtDescSort = function (item) {
        return -new Date(item.createdAt).getTime()
      }

      ctrl.getSortKey = function (sortOption) {
        if (sortOption === 'duration') {
          return ctrl.getDurationSort
        } else if (sortOption === 'mediaInUse') {
          return ctrl.getMediaInUseSort
        } else if (sortOption === 'createdAtDesc') {
          return ctrl.getCreatedAtDescSort
        }
        return sortOption
      }

      ctrl.getPreviewSafeAreaOverlay = function (asset) {
        if (!asset || !asset.orientation || !asset.width || !asset.height) {
          return null
        }

        return buildPreviewSafeAreaOverlay(
          ctrl.previewSafeAreaConfig,
          asset.orientation,
          asset.width,
          asset.height
        )
      }

      ctrl.getPreviewSafeAreaLabel = formatPreviewSafeAreaLabel
      ctrl.getPreviewSafeAreaTooltip = formatPreviewSafeAreaTooltip

      // video modal logic - Bootstrap 5 compatible
      document.addEventListener('click', function (event) {
        if (event.target.classList.contains('modal')) {
          if (event.target.id === 'resolutionModal') {
            ctrl.closeResolutionModal()
          } else {
            ctrl.closeModal()
          }
        }
      })

      function closeModal (element) {
        const modalEvent = new CustomEvent('hide.bs.modal', { bubbles: true })
        element.dispatchEvent(modalEvent)
        if (!modalEvent.defaultPrevented) {
          element.classList.remove('show')
          element.style.display = 'none'
          element.setAttribute('aria-hidden', 'true')
          document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
          document.body.classList.remove('modal-open')
          document.body.style.overflow = ''
        }
      }

      ctrl.closeModal = function () {
        closeModal(document.getElementById('videoModal'))
        document.querySelectorAll('#videoModal video').forEach(v => { v.pause(); v.currentTime = 0 })
      }

      function showModal (element) {
        const modalEvent = new CustomEvent('show.bs.modal', { bubbles: true })
        element.dispatchEvent(modalEvent)
        if (!modalEvent.defaultPrevented) {
          element.classList.add('show')
          element.style.display = 'block'
          element.setAttribute('aria-hidden', 'false')
          const backdrop = document.createElement('div')
          backdrop.className = 'modal-backdrop fade show'
          document.body.appendChild(backdrop)
          document.body.classList.add('modal-open')
          document.body.style.overflow = 'hidden'
        }
      }

      ctrl.openModal = function (item) {
        ctrl.currentItem = item
        const modalElement = document.getElementById('videoModal')
        showModal(modalElement)
        document.querySelectorAll('#videoModal video').forEach(v => { v.play(); v.currentTime = 0 })
      }

      ctrl.getResolutionRows = function () {
        const rows = []
        ;(ctrl.FileMedia || []).forEach(item => {
          const durationMs = ctrl.getDuration(item.id)
          ;(item.assets || []).forEach(asset => {
            if (asset.width && asset.height) {
              const bitrate = (asset.type === 'video' && asset.bytes && durationMs)
                ? (asset.bytes * 8) / (durationMs / 1000) / 1e6
                : null
              rows.push({
                name: item.name,
                id: item.id,
                type: asset.type,
                orientation: asset.orientation,
                width: asset.width,
                height: asset.height,
                aspect: asset.width / asset.height,
                bitrate
              })
            }
          })
        })
        return rows
      }

      ctrl.sortResolutionBy = function (key) {
        if (ctrl.resolutionSort.key === key) {
          ctrl.resolutionSort.reverse = !ctrl.resolutionSort.reverse
        } else {
          ctrl.resolutionSort.key = key
          ctrl.resolutionSort.reverse = false
        }
      }

      ctrl.openResolutionModal = function () {
        const el = document.getElementById('resolutionModal')
        if (el.classList.contains('show')) return
        ctrl.resolutionRows = ctrl.getResolutionRows()
        showModal(el)
      }

      ctrl.closeResolutionModal = function () {
        closeModal(document.getElementById('resolutionModal'))
      }
    }
  }
}
